import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import regresaBienFecha from "../tools/regresaBienFecha";

export default function AsignacionesSarh() {
  const [data, setData] = useState({});
  const [cargando, setCargando] = useState(false);

  const navigate = useNavigate();

  /* ======================================================
     FILTROS
  ====================================================== */

  const [escuelasSeleccionadas, setEscuelasSeleccionadas] = useState([]);
  const [sedesSeleccionadas, setSedesSeleccionadas] = useState([]);
  const [cursosSeleccionados, setCursosSeleccionados] = useState([]);
  const [divisionesSeleccionadas, setDivisionesSeleccionadas] = useState([]);
  const [condicionesSeleccionadas, setCondicionesSeleccionadas] = useState([]);

  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  /*
   * Por defecto se muestran solamente asignaciones
   * vigentes o que todavía no comenzaron.
   */
  const [soloVigentes, setSoloVigentes] = useState(true);

  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState("apellido");
  const [mostrarFiltros, setMostrarFiltros] = useState(true);

  /* ======================================================
     ACORDEONES
  ====================================================== */

  const [escuelasAbiertas, setEscuelasAbiertas] = useState({});
  const [cursosAbiertos, setCursosAbiertos] = useState({});
  const [sedesAbiertas, setSedesAbiertas] = useState({});
  const [divisionesAbiertas, setDivisionesAbiertas] = useState({});

  /* ======================================================
     TRAER DATOS
  ====================================================== */

  const traerDatos = async () => {
    try {
      setCargando(true);

      const res = await axios.post(
        "https://wwwa.isepsantafe.net.ar/expedientes/obtenerAsignaciones2026",
        {}
      );

      const datosRecibidos = res.data?.agrupado || {};

      setData(datosRecibidos);

      /*
       * Cuando se actualizan los datos vuelve al filtro recomendado:
       * solamente vigentes o por iniciar.
       */
      restablecerFiltrosIniciales();

//       console.log("Asignaciones recibidas:", res.data);
    } catch (error) {
      console.error("Error al traer asignaciones:", error);
    } finally {
      setCargando(false);
    }
  };

  /* ======================================================
     FUNCIONES GENERALES
  ====================================================== */

  const normalizarTexto = (valor) =>
    String(valor ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  /*
   * Convierte el valor de horas en un número seguro.
   *
   * Valores como:
   * "-", "--", "", null, undefined o texto inválido
   * se interpretan como cero para evitar NaN.
   *
   * También admite:
   * 10
   * "10"
   * "10,5"
   * "10.5"
   * "10 Hs."
   */
  const obtenerHorasValidas = (valor) => {
    if (
      valor === null ||
      valor === undefined ||
      valor === "" ||
      valor === "-" ||
      valor === "--"
    ) {
      return 0;
    }

    const texto = String(valor).trim();

    if (!texto || texto === "-" || texto === "--") {
      return 0;
    }

    const textoNormalizado = texto
      .replace(",", ".")
      .replace(/[^\d.-]/g, "");

    if (
      !textoNormalizado ||
      textoNormalizado === "-" ||
      textoNormalizado === "." ||
      textoNormalizado === "-."
    ) {
      return 0;
    }

    const numero = Number(textoNormalizado);

    return Number.isFinite(numero) ? numero : 0;
  };

  /*
   * Determina si el dato original representa realmente
   * una cantidad de horas.
   *
   * Permite diferenciar:
   * - 0 real
   * - valor inválido como "-"
   */
  const tieneHorasValidas = (valor) => {
    if (
      valor === null ||
      valor === undefined ||
      valor === "" ||
      valor === "-" ||
      valor === "--"
    ) {
      return false;
    }

    const texto = String(valor).trim();

    if (!texto || texto === "-" || texto === "--") {
      return false;
    }

    const textoNormalizado = texto
      .replace(",", ".")
      .replace(/[^\d.-]/g, "");

    if (
      !textoNormalizado ||
      textoNormalizado === "-" ||
      textoNormalizado === "." ||
      textoNormalizado === "-."
    ) {
      return false;
    }

    return Number.isFinite(Number(textoNormalizado));
  };

  /*
   * Texto final para pantalla, Excel y PDF.
   */
  const formatearHoras = (valor) => {
    if (!tieneHorasValidas(valor)) {
      return "--";
    }

    return `${obtenerHorasValidas(valor)} Hs.`;
  };

  const convertirFecha = (fecha) => {
    if (!fecha) return null;

    const valor =
      typeof fecha === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(fecha)
        ? `${fecha}T00:00:00`
        : fecha;

    const fechaObjeto = new Date(valor);

    if (Number.isNaN(fechaObjeto.getTime())) {
      return null;
    }

    return fechaObjeto;
  };

  const obtenerFechaFinalizacion = (asignacion) =>
    asignacion?.fechaFinalizacion ??
    asignacion?.fechaFinal ??
    asignacion?.fechaFin ??
    null;

  const obtenerHoySinHora = () => {
    const hoy = new Date();

    hoy.setHours(0, 0, 0, 0);

    return hoy;
  };

  /*
   * Se usa sedeCursada como valor principal.
   * Si no viene, recién ahí se utiliza la sede con la que
   * el backend agrupó el registro.
   */
  const obtenerSedeReal = (asignacion, sedeAgrupada) => {
    const sedeCursada = String(
      asignacion?.sedeCursada || ""
    ).trim();

    if (sedeCursada) {
      return sedeCursada;
    }

    return String(sedeAgrupada || "").trim();
  };

  const toggleSeleccion = (valor, setSeleccionados) => {
    setSeleccionados((prev) =>
      prev.includes(valor)
        ? prev.filter((item) => item !== valor)
        : [...prev, valor]
    );
  };

  /*
   * Limpiar filtros muestra también cursos históricos.
   */
  const limpiarFiltros = () => {
    setEscuelasSeleccionadas([]);
    setSedesSeleccionadas([]);
    setCursosSeleccionados([]);
    setDivisionesSeleccionadas([]);
    setCondicionesSeleccionadas([]);

    setFechaDesde("");
    setFechaHasta("");

    setSoloVigentes(false);

    setBusqueda("");
    setOrden("apellido");
  };

  /*
   * Vuelve al comportamiento inicial recomendado.
   */
  const restablecerFiltrosIniciales = () => {
    setEscuelasSeleccionadas([]);
    setSedesSeleccionadas([]);
    setCursosSeleccionados([]);
    setDivisionesSeleccionadas([]);
    setCondicionesSeleccionadas([]);

    setFechaDesde("");
    setFechaHasta("");

    setSoloVigentes(true);

    setBusqueda("");
    setOrden("apellido");
  };

  /* ======================================================
     FILTRO DE VIGENCIA
  ====================================================== */

  const cumpleFiltroVigencia = (asignacion) => {
    if (!soloVigentes) {
      return true;
    }

    const fechaFinal = convertirFecha(
      obtenerFechaFinalizacion(asignacion)
    );

    /*
     * Si no tiene fecha final, se considera vigente.
     * Esto evita ocultar asignaciones abiertas.
     */
    if (!fechaFinal) {
      return true;
    }

    const hoy = obtenerHoySinHora();

    fechaFinal.setHours(23, 59, 59, 999);

    return fechaFinal >= hoy;
  };

  /* ======================================================
     FILTRO DE PERÍODO
  ====================================================== */

  const cumpleFiltroPeriodo = (asignacion) => {
    if (!fechaDesde && !fechaHasta) {
      return true;
    }

    const inicioAsignacion = convertirFecha(
      asignacion?.fechaInicio
    );

    const finalAsignacion = convertirFecha(
      obtenerFechaFinalizacion(asignacion)
    );

    if (!inicioAsignacion && !finalAsignacion) {
      return false;
    }

    const inicioReal = inicioAsignacion || finalAsignacion;
    const finalReal = finalAsignacion || inicioAsignacion;

    const inicioFiltro = fechaDesde
      ? convertirFecha(fechaDesde)
      : null;

    const finalFiltro = fechaHasta
      ? convertirFecha(fechaHasta)
      : null;

    if (inicioFiltro && finalReal < inicioFiltro) {
      return false;
    }

    if (finalFiltro) {
      finalFiltro.setHours(23, 59, 59, 999);

      if (inicioReal > finalFiltro) {
        return false;
      }
    }

    return true;
  };

  /* ======================================================
     FILTRO DE SEDE
  ====================================================== */

  const cumpleFiltroSede = (
    asignacion,
    sedeAgrupada
  ) => {
    if (sedesSeleccionadas.length === 0) {
      return true;
    }

    const sedeReal = normalizarTexto(
      obtenerSedeReal(asignacion, sedeAgrupada)
    );

    return sedesSeleccionadas.some(
      (sede) => normalizarTexto(sede) === sedeReal
    );
  };

  /* ======================================================
     BÚSQUEDA GENERAL
  ====================================================== */

  const cumpleBusqueda = ({
    asignacion,
    escuela,
    curso,
    sedeAgrupada,
    division,
  }) => {
    const textoBuscado = normalizarTexto(busqueda);

    if (!textoBuscado) {
      return true;
    }

    const sedeReal = obtenerSedeReal(
      asignacion,
      sedeAgrupada
    );

    const textoCompleto = normalizarTexto(
      [
        escuela,
        curso,
        sedeReal,
        sedeAgrupada,
        division,
        asignacion?.docenteApellido,
        asignacion?.docenteNombres,
        asignacion?.docenteDni,
        asignacion?.docenteCuil,
        asignacion?.materia,
        asignacion?.horasAsignadas,
        asignacion?.docenteTipo,
        asignacion?.fechaInicio,
        obtenerFechaFinalizacion(asignacion),
      ].join(" ")
    );

    return textoCompleto.includes(textoBuscado);
  };

  /* ======================================================
     ORDENAMIENTO
  ====================================================== */

  const ordenarAsignaciones = (lista) => {
    return [...lista].sort((a, b) => {
      if (orden === "horas") {
        return (
          obtenerHorasValidas(b?.horasAsignadas) -
          obtenerHorasValidas(a?.horasAsignadas)
        );
      }

      if (orden === "materia") {
        return String(a?.materia || "").localeCompare(
          String(b?.materia || ""),
          "es",
          {
            sensitivity: "base",
          }
        );
      }

      if (orden === "inicio") {
        const fechaA =
          convertirFecha(a?.fechaInicio)?.getTime() || 0;

        const fechaB =
          convertirFecha(b?.fechaInicio)?.getTime() || 0;

        return fechaA - fechaB;
      }

      if (orden === "finalizacion") {
        const fechaA =
          convertirFecha(
            obtenerFechaFinalizacion(a)
          )?.getTime() || 0;

        const fechaB =
          convertirFecha(
            obtenerFechaFinalizacion(b)
          )?.getTime() || 0;

        return fechaA - fechaB;
      }

      const nombreA = `${
        a?.docenteApellido || ""
      } ${a?.docenteNombres || ""}`;

      const nombreB = `${
        b?.docenteApellido || ""
      } ${b?.docenteNombres || ""}`;

      return nombreA.localeCompare(nombreB, "es", {
        sensitivity: "base",
      });
    });
  };

  /* ======================================================
     ESCUELAS DISPONIBLES
  ====================================================== */

  const escuelas = useMemo(() => {
    return Object.keys(data).sort((a, b) =>
      a.localeCompare(b, "es", {
        sensitivity: "base",
      })
    );
  }, [data]);

  /* ======================================================
     SEDES DISPONIBLES

     La sede se calcula antes que el curso.
     Respeta:
     - Escuela
     - Vigencia
  ====================================================== */

  const sedesDisponibles = useMemo(() => {
    const sedes = new Set();

    Object.keys(data).forEach((escuela) => {
      if (
        escuelasSeleccionadas.length > 0 &&
        !escuelasSeleccionadas.includes(escuela)
      ) {
        return;
      }

      Object.keys(data[escuela] || {}).forEach(
        (curso) => {
          Object.keys(
            data[escuela]?.[curso] || {}
          ).forEach((sedeAgrupada) => {
            Object.keys(
              data[escuela]?.[curso]?.[
                sedeAgrupada
              ] || {}
            ).forEach((division) => {
              const lista =
                data[escuela]?.[curso]?.[
                  sedeAgrupada
                ]?.[division];

              if (!Array.isArray(lista)) {
                return;
              }

              lista.forEach((asignacion) => {
                if (
                  !cumpleFiltroVigencia(asignacion)
                ) {
                  return;
                }

                const sedeReal = obtenerSedeReal(
                  asignacion,
                  sedeAgrupada
                );

                if (sedeReal) {
                  sedes.add(sedeReal);
                }
              });
            });
          });
        }
      );
    });

    return [...sedes].sort((a, b) =>
      a.localeCompare(b, "es", {
        sensitivity: "base",
      })
    );
  }, [data, escuelasSeleccionadas, soloVigentes]);

  /* ======================================================
     CURSOS DISPONIBLES

     El curso depende ahora de:
     - Escuela
     - Sede
     - Vigencia
  ====================================================== */

  const cursosDisponibles = useMemo(() => {
    const cursos = new Set();

    Object.keys(data).forEach((escuela) => {
      if (
        escuelasSeleccionadas.length > 0 &&
        !escuelasSeleccionadas.includes(escuela)
      ) {
        return;
      }

      Object.keys(data[escuela] || {}).forEach(
        (curso) => {
          let cursoCoincide = false;

          Object.keys(
            data[escuela]?.[curso] || {}
          ).forEach((sedeAgrupada) => {
            Object.keys(
              data[escuela]?.[curso]?.[
                sedeAgrupada
              ] || {}
            ).forEach((division) => {
              const lista =
                data[escuela]?.[curso]?.[
                  sedeAgrupada
                ]?.[division];

              if (!Array.isArray(lista)) {
                return;
              }

              const tieneRegistroValido = lista.some(
                (asignacion) => {
                  if (
                    !cumpleFiltroVigencia(asignacion)
                  ) {
                    return false;
                  }

                  if (
                    !cumpleFiltroSede(
                      asignacion,
                      sedeAgrupada
                    )
                  ) {
                    return false;
                  }

                  return true;
                }
              );

              if (tieneRegistroValido) {
                cursoCoincide = true;
              }
            });
          });

          if (cursoCoincide) {
            cursos.add(curso);
          }
        }
      );
    });

    return [...cursos].sort((a, b) =>
      a.localeCompare(b, "es", {
        sensitivity: "base",
      })
    );
  }, [
    data,
    escuelasSeleccionadas,
    sedesSeleccionadas,
    soloVigentes,
  ]);

  /* ======================================================
     DIVISIONES DISPONIBLES
  ====================================================== */

  const divisionesDisponibles = useMemo(() => {
    const divisiones = new Set();

    Object.keys(data).forEach((escuela) => {
      if (
        escuelasSeleccionadas.length > 0 &&
        !escuelasSeleccionadas.includes(escuela)
      ) {
        return;
      }

      Object.keys(data[escuela] || {}).forEach(
        (curso) => {
          if (
            cursosSeleccionados.length > 0 &&
            !cursosSeleccionados.includes(curso)
          ) {
            return;
          }

          Object.keys(
            data[escuela]?.[curso] || {}
          ).forEach((sedeAgrupada) => {
            Object.keys(
              data[escuela]?.[curso]?.[
                sedeAgrupada
              ] || {}
            ).forEach((division) => {
              const lista =
                data[escuela]?.[curso]?.[
                  sedeAgrupada
                ]?.[division];

              if (!Array.isArray(lista)) {
                return;
              }

              const tieneRegistroValido = lista.some(
                (asignacion) => {
                  if (
                    !cumpleFiltroVigencia(asignacion)
                  ) {
                    return false;
                  }

                  if (
                    !cumpleFiltroSede(
                      asignacion,
                      sedeAgrupada
                    )
                  ) {
                    return false;
                  }

                  return true;
                }
              );

              if (tieneRegistroValido) {
                divisiones.add(division);
              }
            });
          });
        }
      );
    });

    return [...divisiones].sort((a, b) =>
      String(a).localeCompare(
        String(b),
        "es",
        {
          numeric: true,
          sensitivity: "base",
        }
      )
    );
  }, [
    data,
    escuelasSeleccionadas,
    sedesSeleccionadas,
    cursosSeleccionados,
    soloVigentes,
  ]);

  /* ======================================================
     CONDICIONES DISPONIBLES
  ====================================================== */

  const condicionesDisponibles = useMemo(() => {
    const condiciones = new Set();

    Object.keys(data).forEach((escuela) => {
      if (
        escuelasSeleccionadas.length > 0 &&
        !escuelasSeleccionadas.includes(escuela)
      ) {
        return;
      }

      Object.keys(data[escuela] || {}).forEach(
        (curso) => {
          if (
            cursosSeleccionados.length > 0 &&
            !cursosSeleccionados.includes(curso)
          ) {
            return;
          }

          Object.keys(
            data[escuela]?.[curso] || {}
          ).forEach((sedeAgrupada) => {
            Object.keys(
              data[escuela]?.[curso]?.[
                sedeAgrupada
              ] || {}
            ).forEach((division) => {
              if (
                divisionesSeleccionadas.length >
                  0 &&
                !divisionesSeleccionadas.includes(
                  division
                )
              ) {
                return;
              }

              const lista =
                data[escuela]?.[curso]?.[
                  sedeAgrupada
                ]?.[division];

              if (!Array.isArray(lista)) {
                return;
              }

              lista.forEach((asignacion) => {
                if (
                  !cumpleFiltroVigencia(asignacion)
                ) {
                  return;
                }

                if (
                  !cumpleFiltroSede(
                    asignacion,
                    sedeAgrupada
                  )
                ) {
                  return;
                }

                const condicion = String(
                  asignacion?.docenteTipo || ""
                ).trim();

                if (condicion) {
                  condiciones.add(condicion);
                }
              });
            });
          });
        }
      );
    });

    return [...condiciones].sort((a, b) =>
      a.localeCompare(b, "es", {
        sensitivity: "base",
      })
    );
  }, [
    data,
    escuelasSeleccionadas,
    sedesSeleccionadas,
    cursosSeleccionados,
    divisionesSeleccionadas,
    soloVigentes,
  ]);

  /* ======================================================
     LIMPIEZA DE FILTROS DEPENDIENTES
  ====================================================== */

  /*
   * Escuela afecta primero las sedes.
   */
  useEffect(() => {
    setSedesSeleccionadas((prev) =>
      prev.filter((sede) =>
        sedesDisponibles.includes(sede)
      )
    );
  }, [sedesDisponibles]);

  /*
   * Sede afecta después los cursos.
   */
  useEffect(() => {
    setCursosSeleccionados((prev) =>
      prev.filter((curso) =>
        cursosDisponibles.includes(curso)
      )
    );
  }, [cursosDisponibles]);

  useEffect(() => {
    setDivisionesSeleccionadas((prev) =>
      prev.filter((division) =>
        divisionesDisponibles.includes(division)
      )
    );
  }, [divisionesDisponibles]);

  useEffect(() => {
    setCondicionesSeleccionadas((prev) =>
      prev.filter((condicion) =>
        condicionesDisponibles.includes(condicion)
      )
    );
  }, [condicionesDisponibles]);

  /* ======================================================
     DATOS FILTRADOS

     Este objeto es el que utiliza:
     - La pantalla
     - El Excel
     - El PDF
  ====================================================== */

  const dataFiltrada = useMemo(() => {
    const resultado = {};

    Object.keys(data).forEach((escuela) => {
      if (
        escuelasSeleccionadas.length > 0 &&
        !escuelasSeleccionadas.includes(escuela)
      ) {
        return;
      }

      Object.keys(data[escuela] || {}).forEach(
        (curso) => {
          if (
            cursosSeleccionados.length > 0 &&
            !cursosSeleccionados.includes(curso)
          ) {
            return;
          }

          Object.keys(
            data[escuela]?.[curso] || {}
          ).forEach((sedeAgrupada) => {
            Object.keys(
              data[escuela]?.[curso]?.[
                sedeAgrupada
              ] || {}
            ).forEach((division) => {
              if (
                divisionesSeleccionadas.length >
                  0 &&
                !divisionesSeleccionadas.includes(
                  division
                )
              ) {
                return;
              }

              const listaOriginal =
                data[escuela]?.[curso]?.[
                  sedeAgrupada
                ]?.[division];

              if (!Array.isArray(listaOriginal)) {
                return;
              }

              const listaFiltrada =
                listaOriginal.filter(
                  (asignacion) => {
                    if (
                      !cumpleFiltroVigencia(
                        asignacion
                      )
                    ) {
                      return false;
                    }

                    if (
                      !cumpleFiltroSede(
                        asignacion,
                        sedeAgrupada
                      )
                    ) {
                      return false;
                    }

                    if (
                      condicionesSeleccionadas.length >
                        0 &&
                      !condicionesSeleccionadas.includes(
                        String(
                          asignacion?.docenteTipo ||
                            ""
                        ).trim()
                      )
                    ) {
                      return false;
                    }

                    if (
                      !cumpleFiltroPeriodo(
                        asignacion
                      )
                    ) {
                      return false;
                    }

                    if (
                      !cumpleBusqueda({
                        asignacion,
                        escuela,
                        curso,
                        sedeAgrupada,
                        division,
                      })
                    ) {
                      return false;
                    }

                    return true;
                  }
                );

              if (listaFiltrada.length === 0) {
                return;
              }

              /*
               * La agrupación visual y de exportación se realiza
               * utilizando la sede real de cursada.
               *
               * Si dentro de una agrupación del backend existen
               * registros con distintas sedeCursada, se separan.
               */
              listaFiltrada.forEach(
                (asignacion) => {
                  const sedeReal =
                    obtenerSedeReal(
                      asignacion,
                      sedeAgrupada
                    ) || "SIN SEDE";

                  if (!resultado[escuela]) {
                    resultado[escuela] = {};
                  }

                  if (
                    !resultado[escuela][curso]
                  ) {
                    resultado[escuela][curso] = {};
                  }

                  if (
                    !resultado[escuela][curso][
                      sedeReal
                    ]
                  ) {
                    resultado[escuela][curso][
                      sedeReal
                    ] = {};
                  }

                  if (
                    !resultado[escuela][curso][
                      sedeReal
                    ][division]
                  ) {
                    resultado[escuela][curso][
                      sedeReal
                    ][division] = [];
                  }

                  resultado[escuela][curso][
                    sedeReal
                  ][division].push(asignacion);
                }
              );
            });
          });
        }
      );
    });

    /*
     * Ordena las listas después de reagruparlas por sedeCursada.
     */
    Object.keys(resultado).forEach(
      (escuela) => {
        Object.keys(resultado[escuela]).forEach(
          (curso) => {
            Object.keys(
              resultado[escuela][curso]
            ).forEach((sede) => {
              Object.keys(
                resultado[escuela][curso][sede]
              ).forEach((division) => {
                resultado[escuela][curso][sede][
                  division
                ] = ordenarAsignaciones(
                  resultado[escuela][curso][sede][
                    division
                  ]
                );
              });
            });
          }
        );
      }
    );

    return resultado;
  }, [
    data,
    escuelasSeleccionadas,
    sedesSeleccionadas,
    cursosSeleccionados,
    divisionesSeleccionadas,
    condicionesSeleccionadas,
    fechaDesde,
    fechaHasta,
    soloVigentes,
    busqueda,
    orden,
  ]);

  /* ======================================================
     ESTADÍSTICAS
  ====================================================== */

  const estadisticas = useMemo(() => {
    const cursos = new Set();
    const sedes = new Set();
    const docentes = new Set();

    let asignaciones = 0;
    let horas = 0;

    Object.keys(dataFiltrada).forEach(
      (escuela) => {
        Object.keys(
          dataFiltrada[escuela]
        ).forEach((curso) => {
          cursos.add(`${escuela}-${curso}`);

          Object.keys(
            dataFiltrada[escuela][curso]
          ).forEach((sede) => {
            sedes.add(sede);

            Object.keys(
              dataFiltrada[escuela][curso][sede]
            ).forEach((division) => {
              const lista =
                dataFiltrada[escuela][curso][
                  sede
                ][division] || [];

              lista.forEach((asignacion) => {
                asignaciones += 1;

                /*
                 * Si viene "-", "--" o cualquier valor inválido,
                 * obtenerHorasValidas devuelve 0.
                 */
                horas += obtenerHorasValidas(
                  asignacion?.horasAsignadas
                );

                const identificador =
                  asignacion?.docenteDni ||
                  asignacion?.docenteCuil ||
                  `${asignacion?.docenteApellido}-${asignacion?.docenteNombres}`;

                docentes.add(identificador);
              });
            });
          });
        });
      }
    );

    return {
      escuelas: Object.keys(dataFiltrada).length,
      cursos: cursos.size,
      sedes: sedes.size,
      docentes: docentes.size,
      asignaciones,
      horas,
    };
  }, [dataFiltrada]);

  const hayFiltrosActivos =
    escuelasSeleccionadas.length > 0 ||
    sedesSeleccionadas.length > 0 ||
    cursosSeleccionados.length > 0 ||
    divisionesSeleccionadas.length > 0 ||
    condicionesSeleccionadas.length > 0 ||
    fechaDesde ||
    fechaHasta ||
    soloVigentes ||
    busqueda;

  /* ======================================================
     EXPORTAR EXCEL
     MANTIENE EL FORMATO MINISTERIAL
  ====================================================== */

  const exportarExcel = () => {
    const workbook = XLSX.utils.book_new();
    const nombresUtilizados = new Set();

    const obtenerNombreSeguro = (nombre) => {
      let base = String(nombre || "Curso")
        .replace(/[\/\\?*\[\]:]/g, "")
        .substring(0, 25)
        .trim();

      if (!base) {
        base = "Curso";
      }

      let nombreFinal = base;
      let contador = 1;

      while (
        nombresUtilizados.has(nombreFinal)
      ) {
        nombreFinal = `${base}_${contador}`;
        contador += 1;
      }

      nombresUtilizados.add(nombreFinal);

      return nombreFinal;
    };

    Object.keys(dataFiltrada).forEach(
      (escuela) => {
        Object.keys(
          dataFiltrada[escuela]
        ).forEach((curso) => {
          const sheetData = [];

          sheetData.push([escuela]);
          sheetData.push([`CURSO: ${curso}`]);
          sheetData.push([]);

          Object.keys(
            dataFiltrada[escuela][curso]
          ).forEach((sede) => {
            sheetData.push([`SEDE: ${sede}`]);
            sheetData.push([]);

            Object.keys(
              dataFiltrada[escuela][curso][sede]
            ).forEach((division) => {
              const lista =
                dataFiltrada[escuela][curso][
                  sede
                ][division];

              if (!Array.isArray(lista)) {
                return;
              }

              sheetData.push([
                `AULA: ${division}`,
              ]);

              sheetData.push([
                "N°",
                "Apellido y Nombre",
                "CUIL",
                "DNI",
                "Hs. Cátedra",
                "Materia",
                "Inicio",
                "Finalización",
                "Observaciones",
                "Condición",
              ]);

              lista.forEach(
                (asignacion, indice) => {
                  sheetData.push([
                    indice + 1,

                    `${String(
                      asignacion?.docenteApellido ||
                        ""
                    ).toUpperCase()} ${String(
                      asignacion?.docenteNombres ||
                        ""
                    ).toUpperCase()}`.trim(),

                    asignacion?.docenteCuil ??
                      "--",

                    asignacion?.docenteDni ?? "",

                    formatearHoras(
                      asignacion?.horasAsignadas
                    ),

                    asignacion?.materia ?? "",

                    regresaBienFecha(
                      asignacion?.fechaInicio
                    ) ?? "",

                    regresaBienFecha(
                      obtenerFechaFinalizacion(
                        asignacion
                      )
                    ) ?? "",

                    "SUJETO A POLITICA SALARIAL VIGENTE",

                    asignacion?.docenteTipo ?? "",
                  ]);
                }
              );

              sheetData.push([]);
            });
          });

          const worksheet =
            XLSX.utils.aoa_to_sheet(sheetData);

          worksheet["!cols"] = [
            { wch: 7 },
            { wch: 34 },
            { wch: 17 },
            { wch: 14 },
            { wch: 14 },
            { wch: 38 },
            { wch: 14 },
            { wch: 14 },
            { wch: 42 },
            { wch: 20 },
          ];

          XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            obtenerNombreSeguro(curso)
          );
        });
      }
    );

    if (workbook.SheetNames.length === 0) {
      alert(
        "No hay asignaciones para exportar con los filtros actuales."
      );

      return;
    }

    const buffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "asignaciones_filtradas.xlsx"
    );
  };

  /* ======================================================
     EXPORTAR PDF
  ====================================================== */

  const exportarPDF = () => {
    if (
      Object.keys(dataFiltrada).length === 0
    ) {
      alert(
        "No hay asignaciones para exportar con los filtros actuales."
      );

      return;
    }

    const pdf = new jsPDF("l", "pt", "a4");

    const margenIzquierdo = 40;
    const anchoPagina =
      pdf.internal.pageSize.getWidth();

    const altoPagina =
      pdf.internal.pageSize.getHeight();

    let y = 40;

    Object.keys(dataFiltrada).forEach(
      (escuela) => {
        pdf.setFontSize(20);
        pdf.setTextColor(0);

        const tituloEscuela =
          pdf.splitTextToSize(
            escuela.toUpperCase(),
            anchoPagina - 120
          );

        if (
          y + tituloEscuela.length * 26 >
          altoPagina - 60
        ) {
          pdf.addPage();
          y = 40;
        }

        pdf.text(
          tituloEscuela,
          anchoPagina / 2,
          y,
          {
            align: "center",
          }
        );

        y +=
          tituloEscuela.length * 26 + 15;

        Object.keys(
          dataFiltrada[escuela]
        ).forEach((curso) => {
          pdf.setFontSize(16);

          const tituloCurso =
            pdf.splitTextToSize(
              curso.toUpperCase(),
              anchoPagina - 160
            );

          if (
            y + tituloCurso.length * 22 >
            altoPagina - 60
          ) {
            pdf.addPage();
            y = 40;
          }

          pdf.text(
            tituloCurso,
            anchoPagina / 2,
            y,
            {
              align: "center",
            }
          );

          y +=
            tituloCurso.length * 22 + 20;

          Object.keys(
            dataFiltrada[escuela][curso]
          ).forEach((sede) => {
            pdf.setFontSize(13);
            pdf.setTextColor(45);

            if (y > altoPagina - 80) {
              pdf.addPage();
              y = 40;
            }

            pdf.text(
              `SEDE: ${String(
                sede
              ).toUpperCase()}`,
              margenIzquierdo,
              y
            );

            y += 16;

            Object.keys(
              dataFiltrada[escuela][curso][
                sede
              ]
            ).forEach((division) => {
              const lista =
                dataFiltrada[escuela][curso][
                  sede
                ][division];

              const filas = lista.map(
                (asignacion, indice) => [
                  indice + 1,

                  `${String(
                    asignacion?.docenteApellido ||
                      ""
                  ).toUpperCase()} ${String(
                    asignacion?.docenteNombres ||
                      ""
                  ).toUpperCase()}`.trim(),

                  asignacion?.docenteCuil ??
                    "--",

                  asignacion?.docenteDni ?? "",

                  formatearHoras(
                    asignacion?.horasAsignadas
                  ),

                  asignacion?.materia ?? "",

                  regresaBienFecha(
                    asignacion?.fechaInicio
                  ) ?? "",

                  regresaBienFecha(
                    obtenerFechaFinalizacion(
                      asignacion
                    )
                  ) ?? "",

                  "SUJETO A POLITICA SALARIAL VIGENTE",

                  asignacion?.docenteTipo ?? "",
                ]
              );

              autoTable(pdf, {
                startY: y,

                head: [
                  [
                    {
                      content: `AULA: ${division}`,
                      colSpan: 10,

                      styles: {
                        halign: "center",
                        fillColor: [
                          180, 220, 220,
                        ],
                        fontStyle: "bold",
                        fontSize: 12,
                      },
                    },
                  ],

                  [
                    "N°",
                    "Apellido y Nombre",
                    "CUIL",
                    "DNI",
                    "Hs. Cátedra",
                    "Materia",
                    "Inicio",
                    "Finalización",
                    "Observaciones",
                    "Condición",
                  ],
                ],

                body: filas,
                theme: "grid",

                margin: {
                  left: margenIzquierdo,
                  right: 40,
                  bottom: 35,
                },

                headStyles: {
                  fillColor: [
                    220, 220, 220,
                  ],
                  textColor: [0, 0, 0],
                },

                styles: {
                  fontSize: 7.5,
                  cellPadding: 3,
                  valign: "middle",
                },
              });

              y =
                pdf.lastAutoTable.finalY +
                20;
            });
          });
        });
      }
    );

    const cantidadPaginas =
      pdf.internal.getNumberOfPages();

    pdf.setFontSize(10);
    pdf.setTextColor(100);

    for (
      let pagina = 1;
      pagina <= cantidadPaginas;
      pagina += 1
    ) {
      pdf.setPage(pagina);

      pdf.text(
        `Página ${pagina} de ${cantidadPaginas}`,
        anchoPagina / 2,
        altoPagina - 18,
        {
          align: "center",
        }
      );
    }

    pdf.save("asignaciones_filtradas.pdf");
  };

  /* ======================================================
     FUNCIONES DE ACORDEÓN Y CONTEO
  ====================================================== */

  const toggleAcordeon = (
    clave,
    setEstado
  ) => {
    setEstado((prev) => ({
      ...prev,
      [clave]: !prev[clave],
    }));
  };

  const obtenerCantidadCurso = (
    escuela,
    curso
  ) => {
    let cantidad = 0;

    Object.values(
      dataFiltrada?.[escuela]?.[curso] || {}
    ).forEach((divisionesPorSede) => {
      Object.values(
        divisionesPorSede || {}
      ).forEach((lista) => {
        if (Array.isArray(lista)) {
          cantidad += lista.length;
        }
      });
    });

    return cantidad;
  };

  const obtenerCantidadSede = (
    escuela,
    curso,
    sede
  ) => {
    let cantidad = 0;

    Object.values(
      dataFiltrada?.[escuela]?.[curso]?.[
        sede
      ] || {}
    ).forEach((lista) => {
      if (Array.isArray(lista)) {
        cantidad += lista.length;
      }
    });

    return cantidad;
  };

  /* ======================================================
     ESTILOS
  ====================================================== */

  const styles = {
    pagina: {
      minHeight: "100vh",
      padding: 22,
      background: "#f3f6fa",
      fontFamily: "Arial, sans-serif",
      color: "#243247",
    },

    panel: {
      background: "#fff",
      borderRadius: 18,
      padding: 20,
      marginBottom: 18,
      boxShadow:
        "0 8px 25px rgba(38,55,77,0.08)",
      border: "1px solid #e4eaf2",
    },

    cabecera: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 15,
    },

    titulo: {
      margin: 0,
      color: "#27456d",
      fontSize: 26,
    },

    subtitulo: {
      margin: "6px 0 0",
      color: "#758399",
      fontSize: 14,
    },

    acciones: {
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
    },

    boton: {
      border: "none",
      borderRadius: 10,
      padding: "11px 17px",
      cursor: "pointer",
      fontWeight: 700,
    },

    botonAzul: {
      background: "#315f9c",
      color: "#fff",
    },

    botonVerde: {
      background: "#23845b",
      color: "#fff",
    },

    botonRojo: {
      background: "#bd3e47",
      color: "#fff",
    },

    botonClaro: {
      background: "#fff",
      color: "#41536a",
      border: "1px solid #d7e0eb",
    },

    estadisticas: {
      display: "grid",
      gridTemplateColumns:
        "repeat(auto-fit, minmax(140px, 1fr))",
      gap: 12,
      marginTop: 20,
    },

    estadistica: {
      padding: 14,
      borderRadius: 14,
      background: "#f7f9fc",
      border: "1px solid #e2e9f1",
    },

    numero: {
      display: "block",
      color: "#315f9c",
      fontSize: 23,
      fontWeight: 800,
    },

    etiqueta: {
      color: "#758399",
      fontSize: 12,
      fontWeight: 700,
      textTransform: "uppercase",
    },

    filtrosGrid: {
      display: "grid",
      gridTemplateColumns:
        "repeat(auto-fit, minmax(215px, 1fr))",
      gap: 14,
      marginTop: 18,
    },

    campo: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },

    label: {
      fontSize: 12,
      fontWeight: 800,
      color: "#5d6e84",
      textTransform: "uppercase",
    },

    input: {
      width: "100%",
      boxSizing: "border-box",
      border: "1px solid #ccd7e4",
      borderRadius: 10,
      padding: "11px 12px",
      background: "#fbfcfe",
      fontSize: 14,
    },

    grupoChecks: {
      maxHeight: 160,
      overflowY: "auto",
      padding: 8,
      border: "1px solid #dfe7f0",
      borderRadius: 10,
      background: "#f8fafc",
    },

    check: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "7px 5px",
      cursor: "pointer",
      fontSize: 13,
    },

    avisoVigencia: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: 12,
      borderRadius: 10,
      background: soloVigentes
        ? "#eaf7ef"
        : "#fff4e5",

      border: soloVigentes
        ? "1px solid #bce2ca"
        : "1px solid #f2d29d",

      color: soloVigentes
        ? "#24633d"
        : "#80520c",

      fontWeight: 700,
      fontSize: 13,
    },

    escuela: {
      background: "#fff",
      borderRadius: 16,
      overflow: "hidden",
      marginBottom: 18,
      border: "1px solid #e4eaf2",
    },

    escuelaHeader: {
      width: "100%",
      border: "none",
      padding: "17px 20px",
      background: "#315f9c",
      color: "#fff",
      display: "flex",
      justifyContent: "space-between",
      cursor: "pointer",
      fontWeight: 800,
    },

    curso: {
      margin: 14,
      border: "1px solid #dce5ef",
      borderRadius: 13,
      overflow: "hidden",
    },

    cursoHeader: {
      width: "100%",
      border: "none",
      padding: "14px 17px",
      background: "#edf3fa",
      color: "#2e4c71",
      display: "flex",
      justifyContent: "space-between",
      cursor: "pointer",
      fontWeight: 800,
    },

    sede: {
      margin: 12,
      border: "1px solid #e3e8ee",
      borderRadius: 11,
      overflow: "hidden",
    },

    sedeHeader: {
      width: "100%",
      border: "none",
      padding: "12px 15px",
      background: "#f5f7fa",
      display: "flex",
      justifyContent: "space-between",
      cursor: "pointer",
      fontWeight: 700,
    },

    division: {
      margin: 10,
      border: "1px solid #e1e7ee",
      borderRadius: 10,
      overflow: "hidden",
    },

    divisionHeader: {
      width: "100%",
      border: "none",
      padding: "11px 14px",
      background: "#e5ebf1",
      display: "flex",
      justifyContent: "space-between",
      cursor: "pointer",
      fontWeight: 700,
    },

    badge: {
      display: "inline-block",
      padding: "4px 9px",
      borderRadius: 999,
      background: "#eaf1f9",
      color: "#315f9c",
      fontSize: 11,
      fontWeight: 800,
    },

    tablaContenedor: {
      overflowX: "auto",
      width: "100%",
    },

    tabla: {
      width: "100%",
      minWidth: 1100,
      borderCollapse: "collapse",
      fontSize: 13,
    },

    th: {
      padding: "11px 9px",
      background: "#405f82",
      color: "#fff",
      border: "1px solid #d7e0ea",
      textAlign: "left",
    },

    td: {
      padding: "10px 9px",
      border: "1px solid #e0e6ed",
    },

    vacio: {
      background: "#fff",
      borderRadius: 16,
      padding: 35,
      textAlign: "center",
      border: "1px dashed #cbd6e3",
      color: "#748399",
    },
  };

  /* ======================================================
     RENDER
  ====================================================== */

  return (
    <div style={styles.pagina}>
      <section style={styles.panel}>
        <div style={styles.cabecera}>
          <div>
            <h1 style={styles.titulo}>
              Planillas SARH
            </h1>

            <p style={styles.subtitulo}>
              Consulta y exportación de asignaciones
              docentes
            </p>
          </div>

          <div style={styles.acciones}>
            <button
              type="button"
              onClick={traerDatos}
              disabled={cargando}
              style={{
                ...styles.boton,
                ...styles.botonAzul,
                opacity: cargando ? 0.6 : 1,
              }}
            >
              {cargando
                ? "Cargando..."
                : "Actualizar datos"}
            </button>

            <button
              type="button"
              onClick={() =>
                navigate("/Docentes")
              }
              style={{
                ...styles.boton,
                ...styles.botonClaro,
              }}
            >
              Volver
            </button>

            {Object.keys(data).length > 0 && (
              <>
                <button
                  type="button"
                  onClick={exportarExcel}
                  style={{
                    ...styles.boton,
                    ...styles.botonVerde,
                  }}
                >
                  Exportar Excel
                </button>

                <button
                  type="button"
                  onClick={exportarPDF}
                  style={{
                    ...styles.boton,
                    ...styles.botonRojo,
                  }}
                >
                  Exportar PDF
                </button>
              </>
            )}
          </div>
        </div>

        {Object.keys(data).length > 0 && (
          <div style={styles.estadisticas}>
            {[
              [
                "Escuelas",
                estadisticas.escuelas,
              ],
              ["Sedes", estadisticas.sedes],
              ["Cursos", estadisticas.cursos],
              [
                "Docentes",
                estadisticas.docentes,
              ],
              [
                "Asignaciones",
                estadisticas.asignaciones,
              ],
              ["Horas", estadisticas.horas],
            ].map(([etiqueta, valor]) => (
              <div
                key={etiqueta}
                style={styles.estadistica}
              >
                <span style={styles.numero}>
                  {valor}
                </span>

                <span style={styles.etiqueta}>
                  {etiqueta}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {Object.keys(data).length > 0 && (
        <section style={styles.panel}>
          <div style={styles.cabecera}>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                }}
              >
                Filtros de visualización
              </h2>

              <div style={{ marginTop: 10 }}>
                <span style={styles.badge}>
                  Orden: escuela → sede → curso
                </span>
              </div>
            </div>

            <div style={styles.acciones}>
              <button
                type="button"
                onClick={limpiarFiltros}
                style={{
                  ...styles.boton,
                  ...styles.botonClaro,
                }}
              >
                Limpiar filtros y ver históricos
              </button>

              {!soloVigentes && (
                <button
                  type="button"
                  onClick={
                    restablecerFiltrosIniciales
                  }
                  style={{
                    ...styles.boton,
                    ...styles.botonAzul,
                  }}
                >
                  Volver a vigentes
                </button>
              )}

              <button
                type="button"
                onClick={() =>
                  setMostrarFiltros(
                    (prev) => !prev
                  )
                }
                style={{
                  ...styles.boton,
                  ...styles.botonClaro,
                }}
              >
                {mostrarFiltros
                  ? "Ocultar"
                  : "Mostrar"}
              </button>
            </div>
          </div>

          {mostrarFiltros && (
            <>
              <div style={{ marginTop: 16 }}>
                <label
                  style={styles.avisoVigencia}
                >
                  <input
                    type="checkbox"
                    checked={soloVigentes}
                    onChange={(event) =>
                      setSoloVigentes(
                        event.target.checked
                      )
                    }
                  />

                  {soloVigentes
                    ? "Mostrando cursos vigentes o por iniciar"
                    : "Mostrando también cursos finalizados"}
                </label>
              </div>

              <div style={styles.filtrosGrid}>
                <div
                  style={{
                    ...styles.campo,
                    gridColumn: "1 / -1",
                  }}
                >
                  <label style={styles.label}>
                    Búsqueda general
                  </label>

                  <input
                    type="text"
                    value={busqueda}
                    onChange={(event) =>
                      setBusqueda(
                        event.target.value
                      )
                    }
                    placeholder="Docente, DNI, CUIL, materia, sede o curso..."
                    style={styles.input}
                  />
                </div>

                <FiltroChecks
                  titulo="1. Escuela"
                  opciones={escuelas}
                  seleccionados={
                    escuelasSeleccionadas
                  }
                  onToggle={(valor) =>
                    toggleSeleccion(
                      valor,
                      setEscuelasSeleccionadas
                    )
                  }
                  styles={styles}
                />

                <FiltroChecks
                  titulo="2. Sede de cursada"
                  opciones={sedesDisponibles}
                  seleccionados={
                    sedesSeleccionadas
                  }
                  onToggle={(valor) =>
                    toggleSeleccion(
                      valor,
                      setSedesSeleccionadas
                    )
                  }
                  styles={styles}
                />

                <FiltroChecks
                  titulo="3. Curso"
                  opciones={cursosDisponibles}
                  seleccionados={
                    cursosSeleccionados
                  }
                  onToggle={(valor) =>
                    toggleSeleccion(
                      valor,
                      setCursosSeleccionados
                    )
                  }
                  styles={styles}
                />

                <FiltroChecks
                  titulo="4. División"
                  opciones={
                    divisionesDisponibles
                  }
                  seleccionados={
                    divisionesSeleccionadas
                  }
                  onToggle={(valor) =>
                    toggleSeleccion(
                      valor,
                      setDivisionesSeleccionadas
                    )
                  }
                  styles={styles}
                />

                <FiltroChecks
                  titulo="5. Condición"
                  opciones={
                    condicionesDisponibles
                  }
                  seleccionados={
                    condicionesSeleccionadas
                  }
                  onToggle={(valor) =>
                    toggleSeleccion(
                      valor,
                      setCondicionesSeleccionadas
                    )
                  }
                  styles={styles}
                />

                <div style={styles.campo}>
                  <label style={styles.label}>
                    Período desde
                  </label>

                  <input
                    type="date"
                    value={fechaDesde}
                    onChange={(event) =>
                      setFechaDesde(
                        event.target.value
                      )
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.campo}>
                  <label style={styles.label}>
                    Período hasta
                  </label>

                  <input
                    type="date"
                    value={fechaHasta}
                    min={fechaDesde || undefined}
                    onChange={(event) =>
                      setFechaHasta(
                        event.target.value
                      )
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.campo}>
                  <label style={styles.label}>
                    Ordenar por
                  </label>

                  <select
                    value={orden}
                    onChange={(event) =>
                      setOrden(event.target.value)
                    }
                    style={styles.input}
                  >
                    <option value="apellido">
                      Apellido y nombre
                    </option>

                    <option value="horas">
                      Mayor cantidad de horas
                    </option>

                    <option value="materia">
                      Materia
                    </option>

                    <option value="inicio">
                      Fecha de inicio
                    </option>

                    <option value="finalizacion">
                      Fecha de finalización
                    </option>
                  </select>
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {Object.keys(data).length === 0 &&
        !cargando && (
          <div style={styles.vacio}>
            <h3>
              No se cargaron las asignaciones
            </h3>

            <p>
              Presioná “Actualizar datos”.
            </p>
          </div>
        )}

      {Object.keys(data).length > 0 &&
        Object.keys(dataFiltrada).length ===
          0 && (
          <div style={styles.vacio}>
            <h3>No hay resultados</h3>

            <p>
              No existen asignaciones que
              coincidan con los filtros.
            </p>

            <button
              type="button"
              onClick={limpiarFiltros}
              style={{
                ...styles.boton,
                ...styles.botonAzul,
              }}
            >
              Limpiar filtros y ver históricos
            </button>
          </div>
        )}

      {Object.keys(dataFiltrada).map(
        (escuela) => {
          const escuelaAbierta =
            escuelasAbiertas[escuela] !== false;

          return (
            <section
              key={escuela}
              style={styles.escuela}
            >
              <button
                type="button"
                style={styles.escuelaHeader}
                onClick={() =>
                  toggleAcordeon(
                    escuela,
                    setEscuelasAbiertas
                  )
                }
              >
                <span>
                  {escuelaAbierta ? "▼" : "▶"}{" "}
                  {escuela}
                </span>

                <span>
                  {
                    Object.keys(
                      dataFiltrada[escuela]
                    ).length
                  }{" "}
                  cursos
                </span>
              </button>

              {escuelaAbierta &&
                Object.keys(
                  dataFiltrada[escuela]
                ).map((curso) => {
                  const claveCurso = `${escuela}-${curso}`;

                  const cursoAbierto =
                    cursosAbiertos[
                      claveCurso
                    ] !== false;

                  return (
                    <div
                      key={claveCurso}
                      style={styles.curso}
                    >
                      <button
                        type="button"
                        style={
                          styles.cursoHeader
                        }
                        onClick={() =>
                          toggleAcordeon(
                            claveCurso,
                            setCursosAbiertos
                          )
                        }
                      >
                        <span>
                          {cursoAbierto
                            ? "▼"
                            : "▶"}{" "}
                          {curso}
                        </span>

                        <span
                          style={styles.badge}
                        >
                          {obtenerCantidadCurso(
                            escuela,
                            curso
                          )}{" "}
                          asignaciones
                        </span>
                      </button>

                      {cursoAbierto &&
                        Object.keys(
                          dataFiltrada[escuela][
                            curso
                          ]
                        ).map((sede) => {
                          const claveSede = `${claveCurso}-${sede}`;

                          const sedeAbierta =
                            sedesAbiertas[
                              claveSede
                            ] !== false;

                          return (
                            <div
                              key={claveSede}
                              style={styles.sede}
                            >
                              <button
                                type="button"
                                style={
                                  styles.sedeHeader
                                }
                                onClick={() =>
                                  toggleAcordeon(
                                    claveSede,
                                    setSedesAbiertas
                                  )
                                }
                              >
                                <span>
                                  {sedeAbierta
                                    ? "▼"
                                    : "▶"}{" "}
                                  SEDE: {sede}
                                </span>

                                <span
                                  style={
                                    styles.badge
                                  }
                                >
                                  {obtenerCantidadSede(
                                    escuela,
                                    curso,
                                    sede
                                  )}{" "}
                                  asignaciones
                                </span>
                              </button>

                              {sedeAbierta &&
                                Object.keys(
                                  dataFiltrada[
                                    escuela
                                  ][curso][sede]
                                ).map(
                                  (division) => {
                                    const claveDivision = `${claveSede}-${division}`;

                                    const divisionAbierta =
                                      divisionesAbiertas[
                                        claveDivision
                                      ] !== false;

                                    const lista =
                                      dataFiltrada[
                                        escuela
                                      ][curso][sede][
                                        division
                                      ];

                                    return (
                                      <div
                                        key={
                                          claveDivision
                                        }
                                        style={
                                          styles.division
                                        }
                                      >
                                        <button
                                          type="button"
                                          style={
                                            styles.divisionHeader
                                          }
                                          onClick={() =>
                                            toggleAcordeon(
                                              claveDivision,
                                              setDivisionesAbiertas
                                            )
                                          }
                                        >
                                          <span>
                                            {divisionAbierta
                                              ? "▼"
                                              : "▶"}{" "}
                                            DIVISIÓN{" "}
                                            {division}
                                          </span>

                                          <span
                                            style={
                                              styles.badge
                                            }
                                          >
                                            {
                                              lista.length
                                            }{" "}
                                            registros
                                          </span>
                                        </button>

                                        {divisionAbierta && (
                                          <div
                                            style={
                                              styles.tablaContenedor
                                            }
                                          >
                                            <table
                                              style={
                                                styles.tabla
                                              }
                                            >
                                              <thead>
                                                <tr>
                                                  <th
                                                    style={
                                                      styles.th
                                                    }
                                                  >
                                                    N°
                                                  </th>

                                                  <th
                                                    style={
                                                      styles.th
                                                    }
                                                  >
                                                    Apellido
                                                    y nombre
                                                  </th>

                                                  <th
                                                    style={
                                                      styles.th
                                                    }
                                                  >
                                                    DNI
                                                  </th>

                                                  <th
                                                    style={
                                                      styles.th
                                                    }
                                                  >
                                                    CUIL
                                                  </th>

                                                  <th
                                                    style={
                                                      styles.th
                                                    }
                                                  >
                                                    Hs.
                                                  </th>

                                                  <th
                                                    style={
                                                      styles.th
                                                    }
                                                  >
                                                    Materia
                                                  </th>

                                                  <th
                                                    style={
                                                      styles.th
                                                    }
                                                  >
                                                    Sede de
                                                    cursada
                                                  </th>

                                                  <th
                                                    style={
                                                      styles.th
                                                    }
                                                  >
                                                    Inicio
                                                  </th>

                                                  <th
                                                    style={
                                                      styles.th
                                                    }
                                                  >
                                                    Finalización
                                                  </th>

                                                  <th
                                                    style={
                                                      styles.th
                                                    }
                                                  >
                                                    Condición
                                                  </th>
                                                </tr>
                                              </thead>

                                              <tbody>
                                                {lista.map(
                                                  (
                                                    asignacion,
                                                    indice
                                                  ) => (
                                                    <tr
                                                      key={
                                                        asignacion?.id ||
                                                        `${asignacion?.docenteDni}-${asignacion?.materia}-${indice}`
                                                      }
                                                    >
                                                      <td
                                                        style={
                                                          styles.td
                                                        }
                                                      >
                                                        {indice +
                                                          1}
                                                      </td>

                                                      <td
                                                        style={
                                                          styles.td
                                                        }
                                                      >
                                                        <strong>
                                                          {`${String(
                                                            asignacion?.docenteApellido ||
                                                              ""
                                                          ).toUpperCase()} ${String(
                                                            asignacion?.docenteNombres ||
                                                              ""
                                                          ).toUpperCase()}`.trim()}
                                                        </strong>
                                                      </td>

                                                      <td
                                                        style={
                                                          styles.td
                                                        }
                                                      >
                                                        {asignacion?.docenteDni ||
                                                          "--"}
                                                      </td>

                                                      <td
                                                        style={
                                                          styles.td
                                                        }
                                                      >
                                                        {asignacion?.docenteCuil ||
                                                          "--"}
                                                      </td>

                                                      <td
                                                        style={
                                                          styles.td
                                                        }
                                                      >
                                                        {formatearHoras(
                                                          asignacion?.horasAsignadas
                                                        )}
                                                      </td>

                                                      <td
                                                        style={
                                                          styles.td
                                                        }
                                                      >
                                                        {asignacion?.materia ||
                                                          "--"}
                                                      </td>

                                                      <td
                                                        style={
                                                          styles.td
                                                        }
                                                      >
                                                        {
                                                          sede
                                                        }
                                                      </td>

                                                      <td
                                                        style={
                                                          styles.td
                                                        }
                                                      >
                                                        {regresaBienFecha(
                                                          asignacion?.fechaInicio
                                                        )}
                                                      </td>

                                                      <td
                                                        style={
                                                          styles.td
                                                        }
                                                      >
                                                        {regresaBienFecha(
                                                          obtenerFechaFinalizacion(
                                                            asignacion
                                                          )
                                                        )}
                                                      </td>

                                                      <td
                                                        style={
                                                          styles.td
                                                        }
                                                      >
                                                        {asignacion?.docenteTipo ||
                                                          "--"}
                                                      </td>
                                                    </tr>
                                                  )
                                                )}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                )}
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
            </section>
          );
        }
      )}
    </div>
  );
}

/* ======================================================
   COMPONENTE INTERNO PARA LOS CHECKBOXES
====================================================== */

function FiltroChecks({
  titulo,
  opciones,
  seleccionados,
  onToggle,
  styles,
}) {
  return (
    <div style={styles.campo}>
      <label style={styles.label}>
        {titulo}

        {seleccionados.length > 0
          ? ` (${seleccionados.length})`
          : ""}
      </label>

      <div style={styles.grupoChecks}>
        {opciones.length === 0 ? (
          <span
            style={{
              fontSize: 13,
              color: "#77869a",
            }}
          >
            Sin opciones disponibles
          </span>
        ) : (
          opciones.map((opcion) => (
            <label
              key={opcion}
              style={styles.check}
            >
              <input
                type="checkbox"
                checked={seleccionados.includes(
                  opcion
                )}
                onChange={() =>
                  onToggle(opcion)
                }
              />

              <span>{opcion}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}